import { useEffect, useState } from 'react';
import type { UpsertBookingHandle } from '@salonomy/types';
import { Button, Card, Field, Pill, TextInput, Toggle } from '@salonomy/ui';
import {
  useBookingHandle,
  useResidencies,
  useUpsertBookingHandle,
} from '../../data/practitioner.js';
import { ApiClientError } from '../../lib/api.js';
import { useAuth } from '../../lib/auth.js';
import { useToast } from '../../lib/toast.js';

/**
 * WS10(b) — the Settings "Your booking page" surface: claim and manage the practitioner's GLOBAL personal
 * booking handle (book.salonomy.com/p/<handle>). The handle is an IDENTITY-plane fact keyed on the human
 * (resolved before any tenant), so this panel — like PracticePanel — gates on the SIGNED-IN PERSON, not the
 * current tenant's role; any authenticated practitioner can claim one. Self-fetching so the integrator
 * mounts it with no props, mirroring PracticePanel / BillingPanel.
 *
 * STATES (honest, never a bare error for an expected empty case):
 *   • UNCLAIMED (the GET 404, mapped to data:null) → the claim form: handle + display-name inputs.
 *   • CLAIMED → the live page URL with a copy button, a display-name edit, and an Enable/Disable toggle.
 *
 * 409 IS TWO-FACED (same status, different message): "already taken" (a case-insensitive duplicate) is
 * surfaced INLINE on the handle field; "no online-bookable salon" (ineligible caller) is surfaced as a
 * disabled-with-explanation state. We PRECHECK eligibility from the caller's residencies so the 409 isn't
 * the first feedback — see `hasWorkingChair` below for the honest limit of what the client can know.
 */

const PUBLIC_BASE = 'book.salonomy.com/p';

/** A case-insensitive duplicate ("already taken") vs an eligibility 409 ("no online-bookable salon") share
 *  the 409 status, so we disambiguate the two. The server tags the eligibility 409 with a STABLE machine
 *  code (`handle_ineligible`, distinct from the generic `conflict` of a duplicate handle), so we branch on
 *  the code — re-wording the prose can't re-break this. We keep a message fallback for resilience against
 *  an older API that hasn't shipped the code yet. Anything else 409 we treat as the handle being taken. */
function isEligibility409(err: unknown): boolean {
  return (
    err instanceof ApiClientError &&
    err.isConflict &&
    (err.code === 'handle_ineligible' ||
      /online[- ]?book(?:able|ing)|eligible|salon with online/i.test(err.message))
  );
}

export function BookingHandlePanel() {
  const { user } = useAuth();
  const toast = useToast();
  const { data: residencies, isLoading: residenciesLoading } = useResidencies(Boolean(user));
  const { data: handle, isLoading, isError, error } = useBookingHandle(Boolean(user));
  const upsert = useUpsertBookingHandle();

  // Form state for the claim / display-name edit. Seeded from the loaded handle when one exists.
  const [handleInput, setHandleInput] = useState('');
  const [displayName, setDisplayName] = useState('');
  // The inline "already taken" message lives on the handle field; the gated-eligibility message is separate.
  const [takenMsg, setTakenMsg] = useState<string | null>(null);
  const [ineligibleMsg, setIneligibleMsg] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (handle) {
      setHandleInput(handle.handle);
      setDisplayName(handle.displayName);
    } else if (user) {
      // Sensible default for the claim form — the practitioner's own presented name.
      setDisplayName((prev) => prev || user.displayName || '');
    }
  }, [handle, user]);

  // ELIGIBILITY PRECHECK (honest about its limit): the Residency aggregate the console reads does NOT carry
  // a per-leg online_bookable flag, so the client cannot fully re-derive the server's eligibility rule. The
  // most we can know is whether the caller has ANY working chair at all (an active residency or their own
  // studio); with none, claiming can never resolve to bookable slots, so we gate proactively. If they DO
  // have an active chair but none is online-bookable, the server's 409 message fills that gap (surfaced as
  // `ineligibleMsg`) — so the 409 still isn't the first feedback when the cause is "no chair anywhere".
  const hasWorkingChair = (residencies ?? []).some((r) => r.active);
  const claimGated = !handle && !residenciesLoading && !hasWorkingChair;

  const isPending = upsert.isPending;
  const trimmedHandle = handleInput.trim().toLowerCase();
  const trimmedName = displayName.trim();

  async function submit(next: Partial<UpsertBookingHandle> = {}) {
    setTakenMsg(null);
    setIneligibleMsg(null);
    const body: UpsertBookingHandle = {
      handle: next.handle ?? trimmedHandle,
      displayName: next.displayName ?? trimmedName,
      // Default to enabled on a fresh claim; keep the current flag on an update unless explicitly toggled.
      enabled: next.enabled ?? handle?.enabled ?? true,
    };
    try {
      await upsert.mutateAsync(body);
      toast.success(handle ? 'Booking page updated' : 'Booking page claimed');
    } catch (err) {
      if (isEligibility409(err)) {
        // Ineligible: no online-bookable salon behind this human. Show the server's explanation, gated.
        setIneligibleMsg(
          err instanceof ApiClientError ? err.message : 'You need an online-bookable salon first.',
        );
      } else if (err instanceof ApiClientError && err.isConflict) {
        // The other 409: a case-insensitive duplicate of someone else's handle — inline on the field.
        setTakenMsg(err.message || 'That handle is already taken — try another.');
      } else {
        toast.error('Could not save your booking page. Please try again.');
      }
    }
  }

  async function copyUrl() {
    if (!handle) return;
    const url = `${PUBLIC_BASE}/${handle.handle}`;
    try {
      await navigator.clipboard.writeText(`https://${url}`);
      setCopied(true);
      setTimeout(() => setCopied(false), 1600);
    } catch {
      toast.error('Could not copy the link — select and copy it manually.');
    }
  }

  return (
    <Card>
      <div
        style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12 }}
      >
        <div>
          <div className="section-title">Your booking page</div>
          <div className="muted" style={{ fontSize: 13, marginTop: 4, lineHeight: 1.5 }}>
            One personal page clients can book you on — wherever you work. It shows your
            online-bookable services and slots across every salon and your own studio, on a single
            link you own: <span className="mono">{PUBLIC_BASE}/your-handle</span>.
          </div>
        </div>
        {handle ? (
          handle.enabled ? (
            <Pill tone="success">Live</Pill>
          ) : (
            <Pill tone="neutral">Hidden</Pill>
          )
        ) : null}
      </div>

      {isLoading || residenciesLoading ? (
        <div className="muted" style={{ fontSize: 13, padding: '12px 0 0' }}>
          Loading your booking page…
        </div>
      ) : isError ? (
        // Any non-404 GET failure — an honest error, not the claim form (404 was mapped to data:null).
        <div className="muted" style={{ fontSize: 13, padding: '12px 0 0', lineHeight: 1.5 }}>
          We couldn’t load your booking page right now
          {error instanceof ApiClientError ? ` (${error.message})` : ''}. Please refresh and try
          again.
        </div>
      ) : handle ? (
        // ── CLAIMED ──────────────────────────────────────────────────────────────────────────────────
        <div style={{ marginTop: 14, display: 'flex', flexDirection: 'column', gap: 16 }}>
          <div className="settings-row" style={{ alignItems: 'center' }}>
            <div>
              <div className="muted" style={{ fontSize: 12.5 }}>
                Your public link
              </div>
              <div className="mono" style={{ fontWeight: 600 }}>
                {PUBLIC_BASE}/{handle.handle}
              </div>
            </div>
            <Button size="sm" variant="ghost" onClick={copyUrl}>
              {copied ? 'Copied' : 'Copy link'}
            </Button>
          </div>

          <Field label="What clients see (display name)">
            <TextInput
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              placeholder="e.g. Alex at Salonomy"
            />
          </Field>

          <div className="settings-row" style={{ alignItems: 'center' }}>
            <div>
              <div style={{ fontWeight: 600, fontSize: 13.5 }}>
                {handle.enabled ? 'Page is live' : 'Page is hidden'}
              </div>
              <div className="muted" style={{ fontSize: 12.5, lineHeight: 1.5 }}>
                {handle.enabled
                  ? 'Clients can find and book you at the link above.'
                  : 'Your handle is reserved, but the page won’t resolve publicly until you turn it back on.'}
              </div>
            </div>
            <Toggle
              checked={handle.enabled}
              disabled={isPending}
              // Enable/disable is the SAME PUT with `enabled` flipped — there is no separate route.
              onChange={(nextEnabled) => void submit({ enabled: nextEnabled })}
              label={handle.enabled ? 'Live' : 'Hidden'}
              labelHidden
            />
          </div>

          {ineligibleMsg ? (
            <div className="muted" style={{ fontSize: 12.5, color: 'var(--danger, #b42318)' }}>
              {ineligibleMsg}
            </div>
          ) : null}

          <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
            <Button
              size="sm"
              onClick={() => void submit()}
              disabled={isPending || !trimmedName || trimmedName === handle.displayName}
            >
              {isPending ? 'Saving…' : 'Save display name'}
            </Button>
          </div>
        </div>
      ) : claimGated ? (
        // ── INELIGIBLE (proactive, before any 409) ─────────────────────────────────────────────────────
        // No active chair anywhere, so a booking page would resolve to no bookable slots. Explain rather
        // than let the claim 409. (If the cause is instead "chairs exist but none online-bookable", the
        // claim path's `ineligibleMsg` carries the server's exact reason.)
        <div className="muted" style={{ fontSize: 13, padding: '12px 0 0', lineHeight: 1.5 }}>
          You can claim a booking page once you have a chair clients can book online — a residency
          in a salon that takes online bookings, or your own studio. Start your own practice or ask
          a salon to add you, then come back here to claim your handle.
        </div>
      ) : (
        // ── UNCLAIMED (the claim form) ──────────────────────────────────────────────────────────────────
        <div style={{ marginTop: 14, display: 'flex', flexDirection: 'column', gap: 14 }}>
          <Field
            label="Your handle"
            hint={`${PUBLIC_BASE}/${trimmedHandle || 'your-handle'}`}
            error={takenMsg}
          >
            <TextInput
              value={handleInput}
              onChange={(e) => {
                setHandleInput(e.target.value);
                if (takenMsg) setTakenMsg(null);
              }}
              placeholder="your-name"
              autoCapitalize="none"
              spellCheck={false}
            />
          </Field>

          <Field label="What clients see (display name)">
            <TextInput
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              placeholder="e.g. Alex at Salonomy"
            />
          </Field>

          {ineligibleMsg ? (
            <div className="muted" style={{ fontSize: 12.5, color: 'var(--danger, #b42318)' }}>
              {ineligibleMsg}
            </div>
          ) : null}

          <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
            <Button
              onClick={() => void submit()}
              disabled={isPending || !trimmedHandle || !trimmedName}
            >
              {isPending ? 'Claiming…' : 'Claim my booking page'}
            </Button>
          </div>
        </div>
      )}
    </Card>
  );
}
